/**
 * Générateur — Résoudre des problèmes mettant en jeu des fractions
 * Attendu ID : 632 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 3 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey au niveau exercice), déclinés en plusieurs formats UI
 * pour un total de 7 entrées registry.
 *
 * Exercices couverts (voir FICHE_PEDAGO_CLUSTER18 §6.1 à §6.3) :
 *
 *   1. `probleme_additif`         → QCM, Saisie, Texte à trous saisie.
 *   2. `probleme_multiplicatif`   → QCM, Saisie. Périmètre triangle/carré/rectangle.
 *   3. `probleme_verification`    → QCM, Tableau V/F. « Qui dit vrai ? »
 *
 * Règles dures de la fiche (§3, §4) :
 *   - Famille A : 20 couples de fractions à dénominateurs multiples, LCM ≤ 36.
 *   - Famille B : côtés fractionnaires, dén ∈ {3..12}, périmètre simplifié.
 *   - Famille C : Q ∈ {24,30,36,40,48}, au moins 1 vrai et 1 faux.
 *
 * Distracteurs contextualisés (fiche §4) :
 *   1. Oubli du complément à 1
 *   2. Addition des dénominateurs
 *   3. Résultat non simplifié
 *   4. Confusion total/reste
 *   5. Un seul côté au lieu du périmètre
 *   6. Multiplication à deux étages
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import { gcd, simplify, fmtFrac, fmtKatex, fmtItem, fmtItemBrut } from './fraction';
// Audit fractions en texte (2026-09-06) : dans les énoncés et les lignes
// vrai/faux, la fraction s'affiche en KaTeX (fmtItem / fmtItemBrut) ; fmtFrac
// (texte « 3/4 ») ne sert plus qu'aux réponses tapées et aux SVG.

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 632;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ADDITIF_QCM   = 'Résoudre un problème additif de fractions';
const L_ADDITIF_SAISIE = 'Résoudre un problème additif de fractions (saisie)';
const L_ADDITIF_GUIDEE = 'Résoudre un problème additif guidé (texte à trous)';
const L_MULT_QCM       = 'Calculer un périmètre avec un côté fractionnaire';
const L_MULT_SAISIE    = 'Calculer un périmètre avec un côté fractionnaire (saisie)';
const L_VERIF_QCM      = 'Vérifier la cohérence de témoignages fractionnaires';
const L_VERIF_VF       = 'Vérifier la cohérence de témoignages fractionnaires (V/F)';

// Liste fixe de prénoms (règle projet)
const PRENOMS = [
    'Adam', 'Amélie', 'Chloé', 'Hugo', 'Inès', 'Jade',
    'Karim', 'Léo', 'Lucas', 'Manon', 'Sofia', 'Thomas', 'Yanis',
];

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES
// ═══════════════════════════════════════════════════════════

const lcm = (a, b) => (a * b) / gcd(a, b);

/** Additionne a1/b1 + a2/b2, renvoie {num, den} simplifié. */
const addFrac = (a1, b1, a2, b2) => {
    const d = lcm(b1, b2);
    const num = a1 * (d / b1) + a2 * (d / b2);
    return simplify(num, d);
};

/** 1 − num/den, renvoie {num, den} simplifié. */
const subtractFromOne = (num, den) => {
    const s = simplify(num, den);
    return simplify(s.den - s.num, s.den);
};

/** Multiplie a/b × n, renvoie {num, den} simplifié. */
const multiplyFrac = (a, b, n) => simplify(a * n, b);

// ═══════════════════════════════════════════════════════════
// POOLS
// ═══════════════════════════════════════════════════════════

/**
 * POOL_FRAC — 20 couples [a1, b1, a2, b2] où sum < 1
 * et LCM(b1,b2) ≤ 36.
 */
const POOL_FRAC = [
    [1, 4, 3, 8],   [2, 5, 1, 10],  [1, 3, 2, 9],
    [1, 6, 1, 3],   [1, 4, 1, 6],   [2, 3, 1, 6],
    [1, 3, 1, 4],   [3, 8, 1, 4],   [1, 5, 3, 10],
    [2, 9, 1, 3],   [1, 6, 2, 9],   [5, 12, 1, 4],
    [1, 2, 1, 6],   [3, 10, 2, 5],  [1, 3, 1, 9],
    [1, 8, 1, 4],   [2, 5, 3, 10],  [1, 6, 1, 4],
    [1, 3, 5, 12],  [2, 9, 5, 18],
];

// 8 contextes additifs
const CONTEXTES = [
    { template: (n1, f1, n2, f2) => `${n1} mange ${f1} d'un gâteau et ${n2} en mange ${f2}.`, questionReste: 'Quelle part du gâteau reste-t-il\u00a0?', questionSomme: 'Quelle part du gâteau ont-ils mangée au total\u00a0?' },
    { template: (n1, f1, n2, f2) => `${n1} prend ${f1} d'une pizza et ${n2} en prend ${f2}.`, questionReste: 'Quelle part de la pizza reste-t-il\u00a0?', questionSomme: 'Quelle part de la pizza ont-ils prise au total\u00a0?' },
    { template: (n1, f1, n2, f2) => `${n1} cultive ${f1} d'un terrain et ${n2} en cultive ${f2}.`, questionReste: 'Quelle part du terrain reste-t-il non cultivée\u00a0?', questionSomme: 'Quelle part du terrain est cultivée au total\u00a0?' },
    { template: (n1, f1, n2, f2) => `${n1} boit ${f1} d'une bouteille de jus et ${n2} en boit ${f2}.`, questionReste: 'Quelle part de la bouteille reste-t-il\u00a0?', questionSomme: 'Quelle part de la bouteille ont-ils bue au total\u00a0?' },
    { template: (n1, f1, n2, f2) => `Pendant une séance de sport, ${n1} court ${f1} du temps et ${n2} court ${f2} du temps.`, questionReste: 'Quelle fraction du temps reste-t-il\u00a0?', questionSomme: 'Quelle fraction du temps ont-ils couru au total\u00a0?' },
    { template: (n1, f1, n2, f2) => `${n1} dépense ${f1} de son argent de poche et ${n2} en dépense ${f2}.`, questionReste: 'Quelle part de l\'argent de poche reste-t-il\u00a0?', questionSomme: 'Quelle part de l\'argent de poche ont-ils dépensée au total\u00a0?' },
    { template: (n1, f1, n2, f2) => `${n1} lit ${f1} d'un livre et ${n2} en lit ${f2}.`, questionReste: 'Quelle part du livre reste-t-il à lire\u00a0?', questionSomme: 'Quelle part du livre ont-ils lue au total\u00a0?' },
    { template: (n1, f1, n2, f2) => `${n1} parcourt ${f1} d'un trajet en bus et ${n2} en parcourt ${f2}.`, questionReste: 'Quelle part du trajet reste-t-il\u00a0?', questionSomme: 'Quelle part du trajet ont-ils parcourue au total\u00a0?' },
];

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — probleme_additif (QCM, Saisie, Texte à trous)
// ═══════════════════════════════════════════════════════════

/**
 * QCM : problème additif (somme ou reste)
 */
const genAdditifQcm = () => {
    const [a1, b1, a2, b2] = randomChoice(POOL_FRAC);
    const ctx = randomChoice(CONTEXTES);
    const [nom1, nom2] = shuffle(PRENOMS).slice(0, 2);

    const isReste = randInt(0, 1) === 1;
    const sum = addFrac(a1, b1, a2, b2);

    let bonne, question;
    if (isReste) {
        const rem = subtractFromOne(sum.num, sum.den);
        bonne = fmtItem(rem.num, rem.den);
        question = ctx.questionReste;
    } else {
        bonne = fmtItem(sum.num, sum.den);
        question = ctx.questionSomme;
    }

    const enonce = ctx.template(nom1, fmtItem(a1, b1), nom2, fmtItem(a2, b2));

    // Distracteurs
    const distractors = new Set();
    // D1 : somme sans complément (ou inverse)
    if (isReste) {
        distractors.add(fmtItem(sum.num, sum.den));
    } else {
        const rem = subtractFromOne(sum.num, sum.den);
        distractors.add(fmtItem(rem.num, rem.den));
    }
    // D2 : addition des dénominateurs
    distractors.add(fmtItem(a1 + a2, b1 + b2));
    // D3 : résultat non simplifié (avec dén commun)
    const commonDen = lcm(b1, b2);
    const rawNum = a1 * (commonDen / b1) + a2 * (commonDen / b2);
    if (isReste) {
        const remRaw = commonDen - rawNum;
        if (gcd(remRaw, commonDen) > 1) distractors.add(`$\\dfrac{${remRaw}}{${commonDen}}$`);
    } else {
        if (gcd(rawNum, commonDen) > 1) distractors.add(`$\\dfrac{${rawNum}}{${commonDen}}$`);
    }
    // D4 : fallback ±1 au numérateur
    if (isReste) {
        const rem = subtractFromOne(sum.num, sum.den);
        distractors.add(fmtItem(Math.max(1, rem.num + 1), rem.den));
    } else {
        distractors.add(fmtItem(sum.num + 1, sum.den));
    }

    // Ensure exactly 3 unique distractors different from correct
    const dArr = [...distractors].filter(d => d !== bonne).slice(0, 3);
    while (dArr.length < 3) {
        const fallback = fmtItem(randInt(1, 8), randInt(2, 12));
        if (fallback !== bonne && !dArr.includes(fallback)) dArr.push(fallback);
    }

    const texte = `<p>${enonce}</p><p>${question}</p>`;
    const choix = [bonne, ...dArr];
    const payload = makeQcm(choix, bonne, texte, opts(L_ADDITIF_QCM));
    payload._meta.generatorKey = 'probleme_additif';
    return payload;
};

/**
 * Saisie : problème additif
 */
const genAdditifSaisie = () => {
    const [a1, b1, a2, b2] = randomChoice(POOL_FRAC);
    const ctx = randomChoice(CONTEXTES);
    const [nom1, nom2] = shuffle(PRENOMS).slice(0, 2);

    const isReste = randInt(0, 1) === 1;
    const sum = addFrac(a1, b1, a2, b2);

    let bonne, question;
    if (isReste) {
        const rem = subtractFromOne(sum.num, sum.den);
        bonne = fmtFrac(rem.num, rem.den);
        question = ctx.questionReste;
    } else {
        bonne = fmtFrac(sum.num, sum.den);
        question = ctx.questionSomme;
    }

    const enonce = ctx.template(nom1, fmtItem(a1, b1), nom2, fmtItem(a2, b2));
    const texte = `<p>${enonce}</p><p>${question} Donne ta réponse sous forme de fraction simplifiée.</p>`;
    const payload = makeSaisie(bonne, texte, { ...opts(L_ADDITIF_SAISIE), strict: false });
    payload._meta.generatorKey = 'probleme_additif';
    return payload;
};

/**
 * Texte à trous (saisie) : problème additif guidé
 */
const genAdditifGuidee = () => {
    const [a1, b1, a2, b2] = randomChoice(POOL_FRAC);
    const ctx = randomChoice(CONTEXTES);
    const [nom1, nom2, nom3] = shuffle(PRENOMS).slice(0, 3);

    const commonDen = lcm(b1, b2);
    const conv1 = a1 * (commonDen / b1);
    const conv2 = a2 * (commonDen / b2);
    const sumRaw = conv1 + conv2;
    const sum = simplify(sumRaw, commonDen);
    const rem = subtractFromOne(sum.num, sum.den);

    const enonce = ctx.template(nom1, fmtItem(a1, b1), nom2, fmtItem(a2, b2));
    const texte = `<p>${enonce}</p><p>${ctx.questionReste} Complète les étapes de résolution.</p>`;

    // Phrase avec trous : chaque {{...}} est un trou
    // Multi-line : prose plain, maths en $...$, séparés par \n
    const phrase =
        `${nom1} prend $${fmtKatex(a1, b1)} = \\dfrac{{{${conv1}}}}{${commonDen}}$` +
        ` et ${nom2} prend $${fmtKatex(a2, b2)} = \\dfrac{{{${conv2}}}}{${commonDen}}$.\n` +
        `$\\dfrac{{{${conv1}}}}{${commonDen}} + \\dfrac{{{${conv2}}}}{${commonDen}} = \\dfrac{{{${sumRaw}}}}{${commonDen}}` +
        (gcd(sumRaw, commonDen) > 1 ? ` = {{${fmtFrac(sum.num, sum.den)}}}` : '') +
        `$\n` +
        `Il reste $1 - ${fmtKatex(sum.num, sum.den)} = {{${fmtFrac(rem.num, rem.den)}}}$.`;

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_ADDITIF_GUIDEE), mode: 'saisie', strict: false });
    payload._meta.generatorKey = 'probleme_additif';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — probleme_multiplicatif (QCM, Saisie)
// ═══════════════════════════════════════════════════════════

/** Pioche un côté fractionnaire a/b irréductible avec b ∈ {3..12}. */
const pickCote = () => {
    const denPool = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    let b = randomChoice(denPool);
    let a;
    do { a = randInt(1, 3 * b - 1); } while (gcd(a, b) !== 1);
    return { a, b };
};

// Couleurs claires pour le dark-mode (bg-slate-800/50 dans ImageElementEleve)
const SVG_STROKE = 'currentColor';
const SVG_TEXT = 'currentColor';

/** Construit un data URL SVG pour un triangle équilatéral — cotation parallèle au côté gauche. */
const buildTriangleSvg = (coteStr) => {
    // Sommets : haut (90,15), bas-gauche (20,140), bas-droit (160,140)
    // Côté gauche : de (20,140) vers (90,15) — montant vers la droite
    // Angle du côté : atan2(140-15, 90-20) = atan2(125, 70) ≈ 60.7° vers le haut
    // En SVG rotate = sens horaire, on veut le texte qui monte → angle négatif
    const angle = -Math.atan2(140 - 15, 90 - 20) * (180 / Math.PI); // ≈ -60.7°
    // Milieu du côté gauche, décalé vers l'extérieur (perpendiculaire au côté)
    const mx = (90 + 20) / 2 - 14;
    const my = (15 + 140) / 2 - 2;
    const svg = [
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 180 160" width="180" height="160">`,
        `<polygon points="90,15 20,140 160,140" fill="none" stroke="${SVG_STROKE}" stroke-width="2"/>`,
        `<text x="${mx}" y="${my}" text-anchor="middle" font-size="13" fill="${SVG_TEXT}" font-weight="600" transform="rotate(${angle.toFixed(1)},${mx},${my})">${coteStr} cm</text>`,
        `</svg>`,
    ].join('');
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/** Construit un data URL SVG pour un carré — cotation sur le côté droit avec barres de cotation. */
const buildSquareSvg = (coteStr) => {
    const x1 = 20, y1 = 20, side = 100;
    const cx = x1 + side + 15; // ligne de cotation à droite
    const svg = [
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 180 150" width="180" height="150">`,
        `<rect x="${x1}" y="${y1}" width="${side}" height="${side}" fill="none" stroke="${SVG_STROKE}" stroke-width="2"/>`,
        // Barres de cotation droite
        `<line x1="${cx - 4}" y1="${y1}" x2="${cx + 4}" y2="${y1}" stroke="${SVG_TEXT}" stroke-width="1.5"/>`,
        `<line x1="${cx}" y1="${y1}" x2="${cx}" y2="${y1 + side}" stroke="${SVG_TEXT}" stroke-width="1"/>`,
        `<line x1="${cx - 4}" y1="${y1 + side}" x2="${cx + 4}" y2="${y1 + side}" stroke="${SVG_TEXT}" stroke-width="1.5"/>`,
        // Texte le long de la cotation
        `<text x="${cx + 10}" y="${y1 + side / 2 + 5}" text-anchor="start" font-size="13" fill="${SVG_TEXT}" font-weight="600">${coteStr} cm</text>`,
        `</svg>`,
    ].join('');
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/** Construit un data URL SVG pour un rectangle — cotation en bas (longueur) et à droite (largeur). */
const buildRectangleSvg = (longStr, largStr) => {
    const x1 = 20, y1 = 25, w = 160, h = 80;
    const cotY = y1 + h + 15; // cotation sous le rectangle
    const cotX = x1 + w + 15; // cotation à droite
    const svg = [
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 260 155" width="260" height="155">`,
        `<rect x="${x1}" y="${y1}" width="${w}" height="${h}" fill="none" stroke="${SVG_STROKE}" stroke-width="2"/>`,
        // Cotation bas (longueur)
        `<line x1="${x1}" y1="${cotY - 4}" x2="${x1}" y2="${cotY + 4}" stroke="${SVG_TEXT}" stroke-width="1.5"/>`,
        `<line x1="${x1}" y1="${cotY}" x2="${x1 + w}" y2="${cotY}" stroke="${SVG_TEXT}" stroke-width="1"/>`,
        `<line x1="${x1 + w}" y1="${cotY - 4}" x2="${x1 + w}" y2="${cotY + 4}" stroke="${SVG_TEXT}" stroke-width="1.5"/>`,
        `<text x="${x1 + w / 2}" y="${cotY + 16}" text-anchor="middle" font-size="13" fill="${SVG_TEXT}" font-weight="600">${longStr} cm</text>`,
        // Cotation droite (largeur)
        `<line x1="${cotX - 4}" y1="${y1}" x2="${cotX + 4}" y2="${y1}" stroke="${SVG_TEXT}" stroke-width="1.5"/>`,
        `<line x1="${cotX}" y1="${y1}" x2="${cotX}" y2="${y1 + h}" stroke="${SVG_TEXT}" stroke-width="1"/>`,
        `<line x1="${cotX - 4}" y1="${y1 + h}" x2="${cotX + 4}" y2="${y1 + h}" stroke="${SVG_TEXT}" stroke-width="1.5"/>`,
        `<text x="${cotX + 10}" y="${y1 + h / 2 + 5}" text-anchor="start" font-size="13" fill="${SVG_TEXT}" font-weight="600">${largStr} cm</text>`,
        `</svg>`,
    ].join('');
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/** Helper : construit les elements_additionnels avec une image SVG. */
const svgImageElement = (dataUrl) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: dataUrl, maxWidth: 'compact' } }],
});

/**
 * QCM : problème multiplicatif (périmètre)
 */
const genMultiplicatifQcm = () => {
    const subType = randomChoice(['triangle', 'carre', 'rectangle']);
    const cote1 = pickCote();

    let bonne, texte, choix;

    let elAdd;

    if (subType === 'triangle') {
        const per = multiplyFrac(cote1.a, cote1.b, 3);
        bonne = fmtItem(per.num, per.den);
        const coteStr = fmtFrac(cote1.a, cote1.b);
        elAdd = svgImageElement(buildTriangleSvg(coteStr));
        texte = `<p>Un triangle équilatéral a un côté de ${fmtItem(cote1.a, cote1.b)} cm. Quel est son périmètre\u00a0?</p>{{element:0}}`;

        const d1 = fmtItem(cote1.a, cote1.b);
        const d2 = `$\\dfrac{${3 * cote1.a}}{${3 * cote1.b}}$`;
        const perPlus1 = simplify(per.num + 1, per.den);
        const d3 = fmtItem(perPlus1.num, perPlus1.den);
        choix = [bonne, d1, d2, d3];

    } else if (subType === 'carre') {
        const per = multiplyFrac(cote1.a, cote1.b, 4);
        bonne = fmtItem(per.num, per.den);
        const coteStr = fmtFrac(cote1.a, cote1.b);
        elAdd = svgImageElement(buildSquareSvg(coteStr));
        texte = `<p>Un carré a un côté de ${fmtItem(cote1.a, cote1.b)} cm. Quel est son périmètre\u00a0?</p>{{element:0}}`;

        const d1 = fmtItem(cote1.a, cote1.b);
        const d2 = `$\\dfrac{${4 * cote1.a}}{${4 * cote1.b}}$`;
        const perPlus1 = simplify(per.num + 1, per.den);
        const d3 = fmtItem(perPlus1.num, perPlus1.den);
        choix = [bonne, d1, d2, d3];

    } else {
        const cote2 = pickCote();
        const sum = addFrac(cote1.a, cote1.b, cote2.a, cote2.b);
        const per = multiplyFrac(sum.num, sum.den, 2);
        bonne = fmtItem(per.num, per.den);
        const longStr = fmtFrac(cote1.a, cote1.b);
        const largStr = fmtFrac(cote2.a, cote2.b);
        elAdd = svgImageElement(buildRectangleSvg(longStr, largStr));
        texte = `<p>Un rectangle a une longueur de ${fmtItem(cote1.a, cote1.b)} cm et une largeur de ${fmtItem(cote2.a, cote2.b)} cm. Quel est son périmètre\u00a0?</p>{{element:0}}`;

        const d1 = fmtItem(sum.num, sum.den);
        const d2 = fmtItem(cote1.a, cote1.b);
        const perPlus1 = simplify(per.num + 1, per.den);
        const d3 = fmtItem(perPlus1.num, perPlus1.den);
        choix = [bonne, d1, d2, d3];
    }

    // Déduplique si besoin
    const uniqueChoix = [bonne];
    for (const c of choix.slice(1)) {
        if (!uniqueChoix.includes(c)) uniqueChoix.push(c);
    }
    while (uniqueChoix.length < 4) {
        const f = fmtItem(randInt(1, 30), randInt(2, 12));
        if (!uniqueChoix.includes(f)) uniqueChoix.push(f);
    }

    const payload = makeQcm(uniqueChoix, bonne, texte, { ...opts(L_MULT_QCM), elements_additionnels: elAdd });
    payload._meta.generatorKey = 'probleme_multiplicatif';
    return payload;
};

/**
 * Saisie : problème multiplicatif (périmètre triangle ou carré)
 */
const genMultiplicatifSaisie = () => {
    const subType = randomChoice(['triangle', 'carre']);
    const cote1 = pickCote();
    const coteStr = fmtFrac(cote1.a, cote1.b);

    let bonne, texte, elAdd;
    if (subType === 'triangle') {
        const per = multiplyFrac(cote1.a, cote1.b, 3);
        bonne = fmtFrac(per.num, per.den);
        elAdd = svgImageElement(buildTriangleSvg(coteStr));
        texte = `<p>Un triangle équilatéral a un côté de ${fmtItem(cote1.a, cote1.b)} cm. Calcule son périmètre. Donne ta réponse sous forme de fraction simplifiée.</p>{{element:0}}`;
    } else {
        const per = multiplyFrac(cote1.a, cote1.b, 4);
        bonne = fmtFrac(per.num, per.den);
        elAdd = svgImageElement(buildSquareSvg(coteStr));
        texte = `<p>Un carré a un côté de ${fmtItem(cote1.a, cote1.b)} cm. Calcule son périmètre. Donne ta réponse sous forme de fraction simplifiée.</p>{{element:0}}`;
    }

    const payload = makeSaisie(bonne, texte, { ...opts(L_MULT_SAISIE), strict: false, elements_additionnels: elAdd });
    payload._meta.generatorKey = 'probleme_multiplicatif';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — probleme_verification (QCM, Tableau V/F)
// ═══════════════════════════════════════════════════════════

// Objets concrets pour la vérification
const OBJETS_VERIF = ['billes', 'bonbons', 'images', 'autocollants', 'cartes'];

/**
 * Pool de couples vrais (n1/d1 + n2/d2 = 1) avec d1, d2 ∈ {2,3,4,6,8}
 * et n1 ≤ d1, n2 ≤ d2, fractions à numérateur unitaire ou petit.
 */
const COUPLES_VRAIS = [
    [1, 2, 1, 2],     // 1/2 + 1/2
    [1, 3, 2, 3],     // 1/3 + 2/3
    [1, 4, 3, 4],     // 1/4 + 3/4
    [1, 6, 5, 6],     // 1/6 + 5/6
    [1, 8, 7, 8],     // 1/8 + 7/8
    [1, 3, 1, 6],     // 1/3 + 4/6 → non, recalc: 1/3 + 2/3 = 1 mais cross-dén
    [1, 4, 1, 2],     // 1/4 + 1/2 = 3/4 ≠ 1 → non
    [3, 8, 5, 8],     // 3/8 + 5/8 = 1 ✓
    [1, 6, 1, 3],     // 1/6 + 1/3 = 1/2 ≠ 1 → non
    [2, 6, 4, 6],     // 2/6 + 4/6 = 1 ✓
];
// Filter to only valid sum=1 pairs
const VALID_VRAIS = COUPLES_VRAIS.filter(([a1, b1, a2, b2]) => {
    const s = a1 * b2 + a2 * b1;
    const d = b1 * b2;
    return s === d; // sum = 1
});

/**
 * Construit un témoignage cohérent ou incohérent.
 * Utilise uniquement des fractions simples (numérateur ≤ dénominateur,
 * dénominateur ∈ {2,3,4,6,8}).
 * @param {number} Q - quantité totale
 * @param {boolean} shouldBeTrue - si le témoignage doit être cohérent
 * @returns {{ text: string, isTrue: boolean }}
 */
const buildTestimony = (Q, shouldBeTrue, nomImpose = null) => {
    // nomImpose : 3 prénoms DISTINCTS imposés par l'appelant → 3 textes distincts
    // garantis (makeVraiFaux dédoublonne les lignes identiques, un tirage rare
    // de mêmes prénom+fractions faisait tomber le V/F à 2 lignes — S-VF-MIN3).
    const nom = nomImpose || randomChoice(PRENOMS);
    const simpleDens = [2, 3, 4, 6, 8].filter(d => Q % d === 0);

    if (shouldBeTrue) {
        // Piocher un couple valide dont les dénominateurs divisent Q
        const candidates = VALID_VRAIS.filter(([a1, b1, a2, b2]) =>
            Q % b1 === 0 && Q % b2 === 0
        );
        if (candidates.length > 0) {
            const [a1, b1, a2, b2] = randomChoice(candidates);
            const val1 = (a1 * Q) / b1;
            const val2 = (a2 * Q) / b2;
            const text = `${nom} dit : ${fmtItemBrut(a1, b1)} de ${Q} + ${fmtItemBrut(a2, b2)} de ${Q} = ${Q}`;
            return { text, isTrue: true };
        }
        // Ultime fallback
        const text = `${nom} dit : ${fmtItemBrut(1, 2)} de ${Q} + ${fmtItemBrut(1, 2)} de ${Q} = ${Q}`;
        return { text, isTrue: true };
    } else {
        // Faux : piocher 2 fractions simples dont la somme ≠ 1
        const d1 = randomChoice(simpleDens);
        const d2 = randomChoice(simpleDens);
        let n1 = randInt(1, d1 - 1);
        let n2 = randInt(1, d2 - 1);
        // Vérifier que la somme ≠ Q
        let val1 = (n1 * Q) / d1;
        let val2 = (n2 * Q) / d2;
        let attempts = 0;
        while (val1 + val2 === Q && attempts < 20) {
            n2 = randInt(1, d2 - 1);
            val2 = (n2 * Q) / d2;
            attempts++;
        }
        if (val1 + val2 === Q) {
            // Force: use 1/3 + 1/4 = 7/12 ≠ 1
            const text = `${nom} dit : ${fmtItemBrut(1, 3)} de ${Q} + ${fmtItemBrut(1, 4)} de ${Q} = ${Q}`;
            return { text, isTrue: false };
        }
        const text = `${nom} dit : ${fmtItemBrut(n1, d1)} de ${Q} + ${fmtItemBrut(n2, d2)} de ${Q} = ${Q}`;
        return { text, isTrue: false };
    }
};

/**
 * QCM : "Combien de témoignages sont cohérents ?"
 */
const genVerificationQcm = () => {
    const Q = randomChoice([24, 30, 36, 40, 48]);
    const numTrue = randInt(1, 2); // 1 ou 2 vrais sur 3

    const testimonies = [];
    const trueIndices = shuffle([0, 1, 2]).slice(0, numTrue);
    const noms = shuffle([...PRENOMS]).slice(0, 3);

    for (let i = 0; i < 3; i++) {
        const shouldBeTrue = trueIndices.includes(i);
        testimonies.push(buildTestimony(Q, shouldBeTrue, noms[i]));
    }

    const trueCount = testimonies.filter(t => t.isTrue).length;
    const bonne = String(trueCount);
    const objet = randomChoice(OBJETS_VERIF);

    const texte = `<p>On partage ${Q} ${objet} entre plusieurs personnes. Voici trois affirmations :</p>` +
        testimonies.map((t, i) => `<p>${i + 1}. ${t.text}</p>`).join('') +
        `<p>Combien de ces affirmations sont correctes\u00a0?</p>`;

    const choix = ['0', '1', '2', '3'];
    const payload = makeQcm(choix, bonne, texte, opts(L_VERIF_QCM));
    payload._meta.generatorKey = 'probleme_verification';
    return payload;
};

/**
 * Tableau V/F : vérifier chaque témoignage individuellement
 */
const genVerificationVf = () => {
    const Q = randomChoice([24, 30, 36, 40, 48]);
    const numTrue = randInt(1, 2);
    const trueIndices = shuffle([0, 1, 2]).slice(0, numTrue);
    const noms = shuffle([...PRENOMS]).slice(0, 3);

    const testimonies = [];
    for (let i = 0; i < 3; i++) {
        const shouldBeTrue = trueIndices.includes(i);
        testimonies.push(buildTestimony(Q, shouldBeTrue, noms[i]));
    }

    const lignes = testimonies.map(t => t.text);
    const bonnesReponses = testimonies.map(t => t.isTrue ? 0 : 1); // 0=Vrai, 1=Faux

    const objet = randomChoice(OBJETS_VERIF);
    const texte = `<p>On partage ${Q} ${objet}. Pour chaque affirmation, indique si elle est correcte (Vrai) ou incorrecte (Faux).</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VERIF_VF));
    payload._meta.generatorKey = 'probleme_verification';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    probleme_additif_qcm_unique:       genAdditifQcm,
    probleme_additif_texte:            genAdditifSaisie,
    probleme_additif_texte_trou_saisie: genAdditifGuidee,
    probleme_multiplicatif_qcm_unique: genMultiplicatifQcm,
    probleme_multiplicatif_texte:      genMultiplicatifSaisie,
    probleme_verification_qcm_unique:  genVerificationQcm,
    probleme_verification_tableau_vf:  genVerificationVf,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_saisie: 'Saisie trous', relier: 'Relier', tableau_vf: 'Vrai/Faux',
    classer: 'Classer', ordonner: 'Ordonner',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'probleme_additif', label: 'Problème additif de fractions', formatsList: buildFormats('probleme_additif') },
    { id: 'probleme_multiplicatif', label: 'Problème multiplicatif avec fractions', formatsList: buildFormats('probleme_multiplicatif') },
    { id: 'probleme_verification', label: 'Vérification de témoignages', formatsList: buildFormats('probleme_verification') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    gcd, lcm, simplify, fmtFrac, fmtKatex, fmtItem,
    addFrac, subtractFromOne, multiplyFrac,
    PRENOMS, CONTEXTES, POOL_FRAC,
    pickCote, buildTestimony,
};
